import React from 'react';

import { useIsLoggedIn, useIsLoggingIn } from '@order/AuthMachine';
import {
  LoadingAnimation,
  NotFoundView,
  SignedOutView,
} from '@order/components';
import { FlattenedOrderStatuses } from '@order/graphql';
import { useDeliveryOrderInFlight } from '@order/hooks';
import { getDerivedOrderStatuses } from '@order/OrderStatus';

import { ChallengeCelebrationModal } from '../../components/ChallengeCelebration';
import { useOrderStatusNavigation } from './OrderStatus.navigation';
import { OrderStatusCTAs } from './OrderStatusCTAs';
import type { OrderStatusScreenData } from './OrderStatusData';
import { useOrderStatusData } from './OrderStatusData';
import { OrderStatusInfo } from './OrderStatusInfo';
import { OrderStatusLayout } from './OrderStatusLayout';
import { OrderStatusMap } from './OrderStatusMap';

export const OrderStatusScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  // ─────────────────────────────────────────────────────────────────

  if (isLoggingIn) return <LoadingAnimation />;

  if (!isLoggedIn) return <SignedOutView />;

  return <OrderStatusScreenSignedIn />;
};

export const OrderStatusScreenSignedIn = () => {
  const data = useOrderStatusData();
  const { handleSelectRewards } = useOrderStatusNavigation();

  useDeliveryOrderInFlight(); // results later used from cache.

  const { isInvalidOrderStatus } = data;

  if (isInvalidOrderStatus) return <NotFoundView />;

  return (
    <OrderStatusScreenContent {...data} onSelectRewards={handleSelectRewards} />
  );
};

export const OrderStatusScreenContent = ({
  isRenderedOnScreen,
  fetching,
  deliveryAddressName,
  deliveryAddressStreet,
  deliveryAddressSecondaryStreet,
  deliveryAddressCity,
  deliveryAddressState,
  deliveryAddressZipCode,
  deliveryAddressNotes,
  deliveryAddressType,
  deliveryAddressLatitude,
  deliveryAddressLongitude,
  deliveryEstimatedTime,
  deliveryPreference,
  locationName,
  locationAddress,
  locationCity,
  locationState,
  locationZipCode,
  locationLatitude,
  locationLongitude,
  dropoffLocation,
  orderId,
  orderType,
  orderStatus,
  orderTotal,
  orderWantedTime,
  orderCanTrackOrderStatus,
  orderPendingFeedback,
  orderLedger,
  orderLineItems,
  vendorId,
  customerName,
  customerLatitude,
  customerLongitude,
  courierName,
  courierPhone,
  courierLatitude,
  courierLongitude,
  celebration,
  onSelectRewards,
  onReorder,
}: OrderStatusScreenData) => {
  const { isLate, isPast, isPendingFeedback, isTrackable } =
    getDerivedOrderStatuses({
      order: {
        isPendingFeedback: orderPendingFeedback,
        canTrackOrderStatus: orderCanTrackOrderStatus,
      },
      wantedTime: orderWantedTime,
      flattenedOrderStatus: orderStatus,
    });

  if (fetching) return <LoadingAnimation />;

  return (
    <>
      <OrderStatusLayout
        orderType={orderType ?? 'pickup'}
        orderStatus={orderStatus ?? FlattenedOrderStatuses.Failed}
        deliveryAddressName={deliveryAddressName}
        deliveryAddressStreet={deliveryAddressStreet}
        deliveryAddressSecondaryStreet={deliveryAddressSecondaryStreet}
        deliveryAddressCity={deliveryAddressCity}
        deliveryAddressState={deliveryAddressState}
        deliveryAddressZipCode={deliveryAddressZipCode}
        deliveryAddressNotes={deliveryAddressNotes}
        deliveryPreference={deliveryPreference}
        locationName={locationName}
        locationAddress={locationAddress}
        locationCity={locationCity}
        locationState={locationState}
        locationZipCode={locationZipCode}
        dropoffLocation={dropoffLocation}
        ledger={orderLedger ?? {}}
        lineItems={orderLineItems ?? []}
        courierName={courierName}
        courierPhone={courierPhone}
        isLate={isLate}
        info={
          <OrderStatusInfo
            orderId={orderId}
            vendorId={vendorId}
            orderType={orderType}
            orderStatus={orderStatus}
            wantedTime={orderWantedTime}
            estimatedDeliveryTime={deliveryEstimatedTime}
            orderTotal={orderTotal}
            locationName={locationName}
            locationAddress={locationAddress}
            isTrackable={isTrackable}
            isPendingFeedback={isPendingFeedback}
            isPast={isPast}
            onReorder={onReorder}
          />
        }
        footer={
          isPast ? (
            <OrderStatusCTAs
              orderId={orderId}
              orderTotal={orderTotal}
              isPendingFeedback={isPendingFeedback}
              onReorder={onReorder}
            />
          ) : null
        }
      >
        <OrderStatusMap
          orderType={orderType}
          orderStatus={orderStatus}
          locationName={locationName}
          locationLatitude={locationLatitude}
          locationLongitude={locationLongitude}
          customerName={customerName}
          customerLatitude={customerLatitude ?? 0}
          customerLongitude={customerLongitude ?? 0}
          deliveryAddressName={deliveryAddressName}
          deliveryAddressType={deliveryAddressType}
          deliveryAddressLatitude={deliveryAddressLatitude}
          deliveryAddressLongitude={deliveryAddressLongitude}
          courierName={courierName}
          courierLatitude={courierLatitude}
          courierLongitude={courierLongitude}
        />
      </OrderStatusLayout>
      {celebration && isRenderedOnScreen ? (
        <ChallengeCelebrationModal
          celebration={celebration}
          onSelectRewards={onSelectRewards}
        />
      ) : null}
    </>
  );
};
